/**
 * A navegação mora na URL, não no estado.
 *
 * Vale a pena dizer por quê, porque é o que muda o dia de quem usa: com rota de
 * verdade, "manda o link desse atendimento" funciona, o botão voltar do
 * navegador desfaz o último passo, recarregar a página cai no mesmo lugar e a
 * aba aberta em Fluxos continua em Fluxos. Guardar `vista` num objeto perdia
 * tudo isso e ninguém sentia falta até precisar mandar o link.
 *
 * O que é lugar vira caminho (`/atendimentos/12`, `/reunioes/4`). O que é
 * recorte da mesma tela vira busca (`?q=`, `?filtro=`, `?painel=`, `?peticao=1`)
 * — inclusive as sobreposições, que assim também são compartilháveis.
 *
 * Fora daqui: gravar reunião. Aquilo é ação com microfone ligado, não endereço;
 * um link que começa a gravar sozinho seria uma armadilha.
 */
import { useCallback, useMemo } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";

/** Caminho → vista do trilho. É o mesmo nome usado no resto da tela. */
export const VISTA_POR_CAMINHO = {
  atendimentos: "chats",
  tarefas: "tarefas",
  /* A agenda virou três visões de Tarefas (`?visao=dia|semana|mes`) e o endereço
     antigo redireciona para lá — link de tela circula por mensagem no
     escritório, e endereço que morre vira erro no celular de alguém. */
  agenda: "tarefas",
  /* A carteira de contatos: a base do escritório vista de fora da conversa. */
  contatos: "contatos",
  /* As publicações dos diários: a fila de prazo que chega de fora do CRM. */
  publicacoes: "publicacoes",
  /* Os criativos do anúncio: quantos leads e contratos cada um trouxe. */
  criativos: "criativos",
  /* `/fluxos` redireciona para a Central da IA (`?secao=quando`); o mapa aponta
     para a vista de destino para o trilho não acender, por um quadro, um item
     que não existe mais. */
  fluxos: "agentes_ia",
  "agentes-ia": "agentes_ia",
  /* Não é `/astrea`: aquele endereço já é servido pelo próprio Hono (a bancada
     que o agente dirige) e é guardado por área na portaria — a tela do React
     nunca chegaria a carregar ali. */
  "conexao-astrea": "astrea",
  reunioes: "calls",
  documentos: "files",
  equipe: "team",
  rapidas: "rapidas",
  /* Os modelos de contrato; o modelo aberto viaja em `?modelo=`. */
  contratos: "contratos",
  ajustes: "settings",
  /* Os setores lado a lado (master e dev) e a máquina (só o dev). */
  setores: "setores",
  sistema: "sistema",
  observabilidade: "observabilidade",
  /* Assistentes é LUGAR, com endereço próprio: "olha o que o assistente
     respondeu" se manda por mensagem, e a conversa aberta viaja na query. */
  agentes: "agentes",
  /* Notificações é LUGAR, não recorte: tem endereço próprio para mandar por
     mensagem ("olha os prazos de hoje") e para o celular, onde não há trilho e
     o balão do sino não tem de onde nascer. Fora do `RAIL` de propósito — a
     porta é o sino, não um nono ícone. */
  notificacoes: "avisos",
  ajuda: "ajuda",
  /* Os primeiros passos de quem chega: LUGAR (o link vai por mensagem para a
     pessoa recém-cadastrada), fora do `RAIL` — some da vida dela quando o último
     passo se cumpre, e ícone fixo no trilho não sabe sumir. */
  comecar: "comecar",
};

/*
 * Os valores antigos de `?painel=`, traduzidos para a seção que os substituiu.
 *
 * `case` era a aba da ficha (hoje o alto da coluna, que é onde ela já abre),
 * `ai` era a aba do copiloto e `call` a de reuniões. Link com esses valores está
 * em mensagem de WhatsApp de dois meses atrás — some daqui e ele passa a abrir a
 * coluna no lugar errado, calado.
 */
const SECAO_LEGADA = { case: "1", ai: "copiloto", call: "reunioes" };

export const CAMINHO_POR_VISTA = Object.fromEntries(Object.entries(VISTA_POR_CAMINHO).map(([c, v]) => [v, c]));

/*
 * O que ATRAVESSA a troca de tela — e é quase nada, de propósito.
 *
 * A query é "recorte da MESMA tela" (é a regra da casa), e vista diferente é
 * outra tela. Só sobrevive o que não pertence a nenhuma: a bandeja do sino, que
 * abre por cima de qualquer uma porque mora na `Casca`, acima das rotas.
 *
 * Tudo o mais é da tela que ficou para trás — e os nomes SE REPETEM entre elas,
 * que é o que transformava "levar o recorte junto" em defeito: `q` é a busca de
 * sete telas, `filtro` de seis, `secao` de três (Equipe, IA do atendimento,
 * Ajustes), e ainda `visao`, `recorte`, `categoria`, `periodo`, `etapa`, `resp`.
 */
const ATRAVESSAM_A_VISTA = new Set(["avisos"]);

const ligado = (v) => v === "1" || v === "true";

/**
 * A query que sobrevive a sair de `vista` para `destino`.
 *
 * Fora do gancho porque é decisão pura, e decisão pura desta casa se prende em
 * teste (`navegacao.test.jsx`) — o defeito que ela conserta não aparece em
 * nenhum print: a tela de destino simplesmente abre com um recorte que ninguém
 * pediu.
 */
export function buscaQueAtravessa(busca, vista, destino) {
  const params = new URLSearchParams(busca);
  if (destino === vista) return params.toString() ? `?${params}` : "";
  const proxima = new URLSearchParams();
  for (const [chave, valor] of params) if (ATRAVESSAM_A_VISTA.has(chave)) proxima.set(chave, valor);
  return proxima.toString() ? `?${proxima}` : "";
}

/**
 * Tudo que a tela precisa saber sobre onde está, e as funções para ir a outro
 * lugar. Uma peça só, para nenhum componente ter de montar URL na mão.
 */
export function useNavegacao() {
  const local = useLocation();
  const navegar = useNavigate();
  const [busca, definirBusca] = useSearchParams();

  /*
   * O caminho é lido aqui, não com `useParams()`.
   *
   * `useParams` só enxerga a rota casada, e este gancho também é usado ACIMA do
   * <Routes> — é lá que mora o provedor que carrega o caso aberto. Com
   * `useParams` ele recebia um id vazio para sempre, e a conversa ficava em
   * "carregando" enquanto a lista já mostrava a linha selecionada.
   */
  const partes = local.pathname.split("/").filter(Boolean);
  const raiz = partes[0] ?? "atendimentos";
  const vista = VISTA_POR_CAMINHO[raiz] ?? "chats";
  const id = raiz === "atendimentos" ? partes[1] : undefined;
  const gravacaoId = raiz === "reunioes" ? partes[1] : undefined;

  /** Troca um parâmetro de busca preservando os outros. `null` remove. */
  const ajustar = useCallback(
    (mudancas, { substituir = true } = {}) => {
      const proxima = new URLSearchParams(busca);
      for (const [chave, valor] of Object.entries(mudancas)) {
        if (valor === null || valor === undefined || valor === "" || valor === false) proxima.delete(chave);
        else proxima.set(chave, valor === true ? "1" : String(valor));
      }
      definirBusca(proxima, { replace: substituir });
    },
    [busca, definirBusca],
  );

  /**
   * A query que sobrevive a ir para `destino`.
   *
   * Mesma vista: a query inteira — o recorte é o mesmo e tem de continuar de
   * pé. Vista diferente: só o que não é de tela nenhuma.
   */
  const buscaPara = useCallback((destino) => buscaQueAtravessa(busca, vista, destino), [busca, vista]);

  return useMemo(
    () => ({
      vista,
      casoId: id && /^\d+$/.test(id) ? Number(id) : null,
      gravacaoId: gravacaoId && /^\d+$/.test(gravacaoId) ? Number(gravacaoId) : null,
      termo: busca.get("q") ?? "",
      /* Procurar DENTRO da conversa aberta. Nome próprio porque `q` já é a busca
         da mesa e as duas viajam na mesma URL: quem procurou "atestado" na
         conversa e voltou para a lista não deve levar isso para o funil. */
      naConversa: busca.get("naConversa") ?? "",
      /* A barra aberta com o campo ainda vazio. Dois parâmetros porque são duas
         coisas: `procurar` é a barra de pé (apagar o termo não fecha nada) e
         `naConversa` é o que se procura. Um só faria a barra sumir na primeira
         tecla apagada. */
      procurarAberto: ligado(busca.get("procurar")),
      filtro: busca.get("filtro") ?? "Todos",
      /* A célula da matriz etapa × responsável da mesa. É recorte da mesma
         tela, como a pastilha: "os casos da Marina em aguardando documentos"
         se manda por mensagem. `etapa` é a CHAVE do stage; `resp` é o nome do
         responsável (`__fila__` para quem não tem ninguém). */
      /* A ETIQUETA escolhida na mesa: recorte da mesma tela, e por isso query —
         "olha os urgentes" se manda por mensagem como qualquer outro recorte. */
      tagDaMesa: busca.get("tag") ?? "",
      etapaDaMesa: busca.get("etapa") ?? "",
      /* O META-GRUPO da mesa (`aberto | atendendo | concluido | perdido`): o recorte grosso
         que as três visões novas usam. Nome próprio e não `filtro`, porque as
         duas coisas se combinam — "os meus que estão andando" é um link. */
      grupoDaMesa: busca.get("grupo") ?? "",
      /* O CRIATIVO da ficha: a porta que a tela de Criativos abre ("os leads do
         AD04") — recorte da mesa, e por isso query, que se manda por mensagem. */
      criativoDaMesa: busca.get("criativo") ?? "",
      /* O NÚMERO de WhatsApp do setor (id da conexão): "quem fala pelo
         comercial" é recorte da mesma mesa, e por isso query. */
      numeroDaMesa: busca.get("numero") ?? "",
      /* Que VISÃO da mesa: a conversa é o padrão, e sem caso aberto a tela é o
         painel, o kanban ou a tabela do mesmo recorte. Query e não estado local
         pelo motivo de sempre: "olha o kanban dos meus" se manda por mensagem, e
         recarregar cai no mesmo lugar. */
      visaoDaMesa: busca.get("visao") ?? "",
      respDaMesa: busca.get("resp") ?? "",
      /*
       * Que SEÇÃO da coluna o endereço pede — e não que aba, que não existe mais.
       *
       * `aba` era o nome de um parâmetro que apontava para as abas "Caso" e
       * "Reunião", mortas quando o painel virou uma coluna só; sobrou lendo
       * `case`/`ai`/`call` e só o último ainda fazia algo. Agora `?painel=` é a
       * seção (`[data-secao=]` da ficha: `copiloto`, `agora`, `ficha`,
       * `pendencias`, `reunioes`) e a coluna rola até ela. `0` fecha, `1` só
       * abre — é o que a maioria dos links quer dizer.
       *
       * Os três valores antigos continuam sendo lidos: endereço que circula por
       * mensagem no escritório não morre porque a tela mudou de desenho.
       */
      secaoDoPainel: SECAO_LEGADA[busca.get("painel")] ?? busca.get("painel") ?? "",
      /* Qual tarefa o quadro deve apontar. É o que faltava para prazo ter
         endereço: sem isto, aviso de prazo sem caso vinculado, item da agenda e
         pendência do caso não tinham como levar a UMA tarefa — só à lista
         inteira. "Teus prazos desta semana" se mandava por mensagem; "esse
         prazo aqui" não. */
      tarefaApontada: busca.get("tarefa") ?? "",
      /* O quadro recortado por UM caso — é a porta que a ficha do atendimento
         abre ("as tarefas deste caso"). `/api/tarefas?caso=` já filtrava por
         `record_id`; faltava o endereço na tela. */
      casoDoQuadro: busca.get("caso") ?? "",
      /* Com que MODELO o estúdio do agente de IA nasce. É recorte de `novo`, e
         mora na URL porque a escolha vem de fora do estúdio: a pessoa aperta
         "Recepcionista 24/7" no vazio da lista e o estúdio tem de abrir já
         preenchido. Sem isto o botão do vazio abria o formulário em branco e o
         catálogo de modelos aparecia de novo lá dentro — dois cliques para a
         mesma escolha, e a primeira jogada no lixo. */
      modeloDePartida: busca.get("modelo") ?? "",
      /* O recorte de tempo do PLACAR de contratos, em Equipe: "o pódio de 90
         dias" se manda por mensagem como qualquer outro recorte. */
      periodoDoPlacar: busca.get("periodo") ?? "",
      /* A seção da tela de equipe (pessoas | papéis | trilha). Query, não
         estado local: mandar "olha os papéis" por mensagem tem de abrir nos
         papéis, e o botão voltar tem de desfazer a troca de aba. */
      secao: busca.get("secao") ?? "",
      /* O intervalo da agenda. Mora na URL porque "a semana que vem" é recorte da
         mesma tela — e porque "olha a semana do dia 25" se manda por mensagem. */
      de: busca.get("de") ?? "",
      ate: busca.get("ate") ?? "",
      painelAberto: busca.get("painel") !== "0",
      /* O painel nasce aberto onde EMPURRA. Onde ele flutua por cima (celular,
         tablet estreito) só aparece se alguém pediu — sem isto, tocar numa
         conversa no telefone desenhava a aba Copiloto sobre a conversa inteira,
         com um ✕ de 24px como única saída. */
      painelPedido: busca.get("painel") != null,
      peticaoAberta: ligado(busca.get("peticao")),
      /* O palco da IA — a conversa inteira virando o que o agente está fazendo.
         Nasce ABERTO quando há delegação viva (é o estado do atendimento, não um
         extra), e `?palco=0` volta para a conversa. Query, e não estado local:
         "olha o que a IA está fazendo no caso 12" se manda por mensagem, e o
         botão voltar desfaz. */
      palcoAberto: busca.get("palco") !== "0",
      /* A conferência dos documentos é LUGAR dentro do caso: "confere os
         documentos da Marina" se manda por mensagem, e o voltar do celular
         fecha, como fecha a petição. */
      conferirAberto: ligado(busca.get("conferir")),
      /* Qual fato a petição deve mostrar, e qual gravação a aba Reunião deve
         abrir aberta. São endereço: "→ fato na petição" e "ver transcrição"
         prometiam levar a UM item e só trocavam de tela, deixando a pessoa
         procurando o que ela já tinha apontado. */
      fatoApontado: busca.get("fato") ?? "",
      gravacaoApontada: busca.get("gravacao") ?? "",
      delegarAberto: ligado(busca.get("delegar")),
      transferirAberto: ligado(busca.get("transferir")),
      /*
       * O gerenciador de etiquetas carrega o ID do caso, e não um "1".
       *
       * As outras sobreposições do caso valem para o caso ABERTO, então `=1`
       * basta. Esta não: ela abre também pelo menu de contexto de uma LINHA da
       * lista, que quase nunca é a conversa aberta — etiquetar sem abrir o caso
       * é o gesto de quem está varrendo a mesa. Com `=1` ela etiquetaria o caso
       * errado, calada.
       */
      etiquetasDe: Number(busca.get("etiquetas")) || 0,
      avisosAbertos: ligado(busca.get("avisos")),
      /* As outras quatro sobreposições da mesa. Moravam em `useState`, e na mesma
         tela o mesmo gesto tinha dois resultados: o voltar fechava a petição e
         atravessava o editor de fluxo. No celular isso é grave — o voltar do
         Android é O gesto de fechar. De carona, "abre no anexo tal" virou link. */
      fluxoAberto: busca.get("fluxo") ?? "",
      /* O agente de IA aberto no ESTÚDIO ("novo" ou o id). Virou caminho
         (`/agentes-ia/12`) porque o estúdio é página inteira, não sobreposição —
         e "olha o agente das gestantes" continua indo por mensagem. A query
         antiga (`?agenteIa=`) segue lida para link velho não morrer. */
      agenteIaAberto: (raiz === "agentes-ia" ? partes[1] : undefined) ?? busca.get("agenteIa") ?? "",
      pessoaAberta: busca.get("pessoa") ?? "",
      acessoAberto: busca.get("acesso") ?? "",
      anexoAberto: busca.get("anexo"),

      /*
       * O recorte das outras vistas.
       *
       * Mesma razão da busca da mesa: recarregar cai no mesmo lugar e a vista
       * montada ("pasta da Angélica · só PDF · últimos 7 dias") se manda por
       * mensagem. `q` é de propósito o mesmo nome em todas — é a MESMA pergunta,
       * e assim a busca acompanha quem troca de tela.
       */
      familia: busca.get("familia") ?? "",
      periodo: busca.get("periodo") ?? "",
      pasta: busca.get("pasta") ?? "",
      /* A pasta DENTRO do cliente ("CTPS", "Provas"). `sem` é o que está solto
         na raiz — recorte que também se manda por mensagem. */
      em: busca.get("em") ?? "",
      apenas: busca.get("apenas") ?? "",
      /* A categoria aberta em Rápidas. Nome próprio porque `apenas` e `familia`
         já são recorte de Documentos, e as três viajam na mesma URL. */
      categoriaAberta: busca.get("categoria") ?? "",
      /* A pastilha da caixa do aparelho tem nome próprio: `filtro` já é a
         pastilha da mesa, e as duas viajam juntas na mesma URL. */
      recorteDaCaixa: busca.get("caixa") ?? "todas",
      /* A origem escolhida na tela de Notificações. Nome próprio pelo mesmo
         motivo: `apenas` já é o tipo de arquivo em Documentos, e as duas viajam
         na mesma URL — "só prazo" viraria "só arquivo .prazo" ao trocar de tela. */
      recorteDosAvisos: busca.get("aviso") ?? "todos",
      recorteDaEquipe: busca.get("recorte") ?? "",
      /* A folha que dá acesso a várias pessoas de uma vez. `todos` abre com
         todos marcados; um id abre com o foco naquela pessoa — e a sentinela é
         a palavra, não `1`, porque "1" também é id de gente. Query e não estado
         porque é sobreposição, como `?pessoa=` e `?acesso=` ao lado. */
      ativacaoAberta: busca.get("ativar") ?? "",
      /* Qual conversa com o assistente está aberta. Query e não caminho: é
         recorte da MESMA tela (a lateral continua ali), como `?peticao=1` e
         `?fluxo=`. Nome próprio porque `q` e `filtro` já são de outras vistas. */
      conversaDoAgente: busca.get("conversa") ?? "",

      /* O caminho de agora, como está na barra do navegador. Quem precisa é
         quem GUARDA lugar (os recentes da paleta): montar de novo a partir de
         `vista` perderia o id do atendimento e a query. */
      caminho: local.pathname,

      /*
       * A query segue junto DENTRO da mesma vista, e é deixada para trás ao
       * trocar de tela.
       *
       * Ela seguia sempre, com a promessa de que "parâmetro que a vista de
       * destino não lê é inerte". Não é: os nomes se repetem entre telas, e o
       * destino LÊ o parâmetro do vizinho como se fosse dele. Buscar "atestado"
       * na mesa e ir para Equipe abria "Nada com esse recorte" sobre uma busca
       * que ninguém digitou ali; sair de `/equipe?secao=papeis` para Ajustes
       * pedia a seção "papeis", que lá não existe; `?visao=mes` de Tarefas caía
       * em Contatos. Quem quer voltar ao recorte anterior tem o botão voltar do
       * navegador, que é exatamente para isso — e ele preserva a URL inteira.
       *
       * Dentro da MESMA vista nada muda: abrir um caso da lista filtrada
       * mantém o filtro, e fechar a transcrição volta para a busca de reuniões.
       */
      irParaVista: (v) => navegar(`/${CAMINHO_POR_VISTA[v] ?? "atendimentos"}${buscaPara(v)}`),
      irParaCaso: (casoId) => navegar(`/atendimentos/${casoId}${buscaPara("chats")}`),
      voltarParaLista: () => navegar(`/atendimentos${buscaPara("chats")}`),
      irParaGravacao: (gid) => navegar(`/reunioes/${gid}`),
      /*
       * A query de agora MENOS o que se está fechando, pronta para colar num
       * caminho.
       *
       * Fechar a sobreposição e navegar eram duas coisas na mesma linha —
       * `ajustar({avisos:null})` e depois `irParaCaso()`, que remonta a URL com
       * a query capturada na renderização, `avisos=1` incluído. A última
       * navegação ganhava, e o balão do sino reabria em cima da conversa que
       * acabou de ser aberta. Com isto é UMA navegação, já sem o parâmetro.
       */
      buscaSem: (...chaves) => {
        const proxima = new URLSearchParams(busca);
        for (const c of chaves) proxima.delete(c);
        return proxima.toString() ? `?${proxima}` : "";
      },
      /* O caminho que o SERVIDOR mandou, seguido como está. A pendência já vem
         com `link` pronto (`/atendimentos/12?peticao=1`, `/reunioes/4`): montar
         de novo aqui seria a tela adivinhando onde cada origem se resolve. */
      ir: (caminho) => navegar(caminho),
      voltar: () => navegar(-1),
      ajustar,
    }),
    [ajustar, busca, buscaPara, gravacaoId, id, local.pathname, navegar, vista],
  );
}
